import { useCallback, useEffect, useState } from 'react';
import { Camera, CameraOff } from 'lucide-react';

type CameraCaptureInfo = {
  width?: number;
  height?: number;
  frameRate?: number;
  deviceId?: string;
  label?: string;
  facingMode?: string;
};

type CameraStreamStats = {
  codec?: string;
  encoderImplementation?: string;
  hardwareAccelerated?: boolean;
  framesPerSecond?: number;
  framesEncoded?: number;
  bitrateBps?: number;
  width?: number;
  height?: number;
  qualityLimitationReason?: string;
  rttMs?: number;
  packetsLostPct?: number;
};

export interface UseCameraStatusOptions {
  /**
   * When false, the hook still attaches the callbacks (so the
   * underlying `<RemoteControl />` works) but renders a null pill.
   */
  enabled?: boolean;
  /** Compact pill layout (no inline resolution / codec labels). */
  compact?: boolean;
}

export interface UseCameraStatusResult {
  /** Spread onto the `<RemoteControl />` props. */
  cameraProps: {
    onCameraDemandChange: (
      active: boolean,
      granted?: boolean,
      camera?: CameraCaptureInfo,
    ) => void;
    onCameraStats: (stats: CameraStreamStats | null) => void;
  };
  /** Rendered status pill, or `null` while the device isn't pulling the camera. */
  pill: React.ReactNode;
  /** True while an iOS app inside the sim is requesting the camera. */
  active: boolean;
  /**
   * Most recent permission outcome: `true` after the user granted,
   * `false` after a denial / device error, `undefined` while no
   * prompt has resolved yet during the current active window.
   */
  granted: boolean | undefined;
}

/**
 * Reusable hook that owns the camera-status indicator state. The
 * device drives everything — the pill only appears while an app
 * is actively pulling the camera. WebRTC chooses the
 * capture dimensions. Use on any route that renders a `<RemoteControl />`.
 */
export function useCameraStatus(
  options: UseCameraStatusOptions = {},
): UseCameraStatusResult {
  const { enabled = true, compact = false } = options;

  const [cameraDemandActive, setCameraDemandActive] = useState(false);
  const [cameraGranted, setCameraGranted] = useState<boolean | undefined>(undefined);
  const [cameraCapture, setCameraCapture] = useState<CameraCaptureInfo | undefined>(undefined);
  const [cameraStats, setCameraStats] = useState<CameraStreamStats | null>(null);

  useEffect(() => {
    if (!cameraDemandActive) {
      setCameraGranted(undefined);
      setCameraCapture(undefined);
      setCameraStats(null);
    }
  }, [cameraDemandActive]);

  const handleCameraDemandChange = useCallback(
    (active: boolean, granted?: boolean, camera?: CameraCaptureInfo) => {
      setCameraDemandActive(active);
      if (granted !== undefined) setCameraGranted(granted);
      if (camera) setCameraCapture(camera);
    },
    [],
  );

  const handleCameraStats = useCallback((stats: CameraStreamStats | null) => {
    setCameraStats(stats);
  }, []);

  let pill: React.ReactNode = null;
  if (enabled && cameraDemandActive) {
    const denied = cameraGranted === false;
    const captureLabel =
      !denied && cameraCapture?.width && cameraCapture?.height
        ? `${cameraCapture.width}×${cameraCapture.height}${
            cameraCapture.frameRate
              ? ` · ${Math.round(cameraCapture.frameRate)} fps`
              : ''
          }`
        : undefined;
    const limited =
      cameraStats?.qualityLimitationReason &&
      cameraStats.qualityLimitationReason !== 'none';

    const tooltipLines: string[] = [];
    if (denied) {
      tooltipLines.push(
        'App is asking for the camera but the browser denied it (showing black frames)',
      );
    } else {
      if (cameraCapture?.label) tooltipLines.push(`Camera · ${cameraCapture.label}`);
      if (captureLabel) tooltipLines.push(`Capture · ${captureLabel}`);
      if (cameraStats?.codec) {
        const hwTag =
          cameraStats.hardwareAccelerated === true
            ? 'HW'
            : cameraStats.hardwareAccelerated === false
              ? 'SW'
              : '';
        const impl = cameraStats.encoderImplementation;
        tooltipLines.push(
          `Codec · ${cameraStats.codec}${hwTag ? ` · ${hwTag}` : ''}${impl ? ` (${impl})` : ''}`,
        );
      }
      if (cameraStats?.width && cameraStats?.height) {
        const fps = cameraStats.framesPerSecond
          ? ` @ ${cameraStats.framesPerSecond.toFixed(0)} fps`
          : '';
        tooltipLines.push(`Sending · ${cameraStats.width}×${cameraStats.height}${fps}`);
      }
      if (cameraStats?.bitrateBps && cameraStats.bitrateBps > 0) {
        tooltipLines.push(`Bitrate · ${(cameraStats.bitrateBps / 1_000_000).toFixed(2)} Mbps`);
      }
      if (typeof cameraStats?.rttMs === 'number') {
        tooltipLines.push(`RTT · ${cameraStats.rttMs.toFixed(0)} ms`);
      }
      if (
        typeof cameraStats?.packetsLostPct === 'number' &&
        cameraStats.packetsLostPct > 0
      ) {
        tooltipLines.push(`Loss · ${cameraStats.packetsLostPct.toFixed(2)}%`);
      }
      if (limited) {
        tooltipLines.push(
          `Limited by ${cameraStats?.qualityLimitationReason} — encoder dropped resolution to keep frame rate`,
        );
      }
    }
    const tooltip =
      tooltipLines.length > 0
        ? tooltipLines.join('\n')
        : 'An app on the device is using your camera';

    const containerCls = denied
      ? 'border-destructive/40 bg-destructive/10 text-destructive'
      : limited
        ? 'border-yellow-500/40 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
        : 'border-primary/40 bg-primary/10 text-primary';

    const pillBody = (
      <>
        {denied ? <CameraOff className="h-3 w-3" /> : <Camera className="h-3 w-3" />}
        {captureLabel && !compact && (
          <span className="text-xs font-medium tabular-nums">{captureLabel}</span>
        )}
        {!denied && !compact && cameraStats?.codec && (
          <span
            className="rounded bg-background/60 px-1 text-[10px] font-semibold uppercase tracking-wide"
            aria-hidden
          >
            {cameraStats.codec}
            {cameraStats.hardwareAccelerated === true ? ' · HW' : ''}
          </span>
        )}
      </>
    );

    pill = (
      <output
        className={`flex items-center gap-1.5 rounded-md border bg-background/95 px-2 py-1.5 shadow-sm backdrop-blur ${containerCls}`}
        title={tooltip}
      >
        <span className="sr-only">
          {denied ? 'Device camera access denied' : 'Device using camera'}
        </span>
        {pillBody}
      </output>
    );
  }

  return {
    cameraProps: {
      onCameraDemandChange: handleCameraDemandChange,
      onCameraStats: handleCameraStats,
    },
    pill,
    active: cameraDemandActive,
    granted: cameraGranted,
  };
}
